import { useState, useRef, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { Mic, X, Check, RotateCcw, Play, Pause, Copy, ChevronDown } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Compact country list (ISO alpha-2). Flag via regional-indicator emoji.
const COUNTRIES = [
  "IT","US","GB","FR","DE","ES","NL","BE","CH","AT","PT","IE","PL","SE","NO","DK","FI",
  "CZ","GR","HU","RO","HR","SI","SK","RU","UA","TR","BR","AR","MX","CA","AU","NZ","JP",
  "CN","KR","IN","AE","IL","EG","MA","ZA","TH","SG","ID","PH","VN","CL","CO","PE",
];
const flagEmoji = (cc) => cc ? cc.toUpperCase().replace(/./g, c => String.fromCodePoint(127397 + c.charCodeAt())) : "🏳️";
const countryName = (cc, lang) => {
  try { return new Intl.DisplayNames([lang || "it"], { type: "region" }).of(cc) || cc; } catch { return cc; }
};

const RATING_EMOJI = ["😞","😞","😕","😐","🙂","🙂","😊","😃","😍","🤩","🥰"];

const T = {
  it: { how: "Come funziona", howText: "Racconta in un minuto com'è andata, saluta chi verrà dopo di te, o lascia un pensiero all'host. Sarà lui ad ascoltarlo per primo. Niente nome vero richiesto, scegli un nickname o resta anonimo. Messaggi offensivi o diffamatori non verranno mai pubblicati.", age: "Confermo di avere più di 18 anni", gotit: "Ho capito, registro", tapRec: "Tocca per registrare", recording: "Registrazione…", restart: "Ricomincia", cont: "Continua", nickname: "Nickname", nickPh: "Ospite anonimo", country: "Paese", rating: "Il tuo voto", consentQ: "Possiamo pubblicare il tuo saluto sulla pagina della casa?", yes: "Sì, pubblicalo", no: "No, resta privato", sending: "Invio…", savedTitle: "Fatto, grazie!", saveCode: "Salva questo codice se vuoi richiedere la rimozione in futuro: scrivi a", withCode: "con il codice", copy: "Copia codice", copied: "Copiato!", close: "Chiudi", micDenied: "Permesso microfono negato. Attivalo nelle impostazioni del browser.", selCountry: "Seleziona un paese" },
  en: { how: "How it works", howText: "Tell us in one minute how it went, greet whoever comes after you, or leave a thought for the host. They'll be the first to listen. No real name required, pick a nickname or stay anonymous. Offensive or defamatory messages will never be published.", age: "I confirm I am over 18", gotit: "Got it, let's record", tapRec: "Tap to record", recording: "Recording…", restart: "Restart", cont: "Continue", nickname: "Nickname", nickPh: "Anonymous guest", country: "Country", rating: "Your rating", consentQ: "Can we publish your greeting on the property page?", yes: "Yes, publish it", no: "No, keep it private", sending: "Sending…", savedTitle: "Done, thank you!", saveCode: "Save this code if you want to request removal later: write to", withCode: "with the code", copy: "Copy code", copied: "Copied!", close: "Close", micDenied: "Microphone permission denied. Enable it in your browser settings.", selCountry: "Select a country" },
  fr: { how: "Comment ça marche", howText: "Racontez en une minute comment ça s'est passé, saluez ceux qui viendront après vous, ou laissez un mot à l'hôte. Il l'écoutera en premier. Pas de vrai nom requis, choisissez un pseudo ou restez anonyme. Les messages offensants ou diffamatoires ne seront jamais publiés.", age: "Je confirme avoir plus de 18 ans", gotit: "Compris, j'enregistre", tapRec: "Toucher pour enregistrer", recording: "Enregistrement…", restart: "Recommencer", cont: "Continuer", nickname: "Pseudo", nickPh: "Invité anonyme", country: "Pays", rating: "Votre note", consentQ: "Pouvons-nous publier votre message sur la page du logement ?", yes: "Oui, publiez-le", no: "Non, privé", sending: "Envoi…", savedTitle: "Fait, merci !", saveCode: "Enregistrez ce code si vous souhaitez demander la suppression plus tard : écrivez à", withCode: "avec le code", copy: "Copier le code", copied: "Copié !", close: "Fermer", micDenied: "Permission micro refusée. Activez-la dans les paramètres du navigateur.", selCountry: "Choisir un pays" },
  es: { how: "Cómo funciona", howText: "Cuenta en un minuto cómo fue, saluda a quien venga después de ti, o deja un mensaje al anfitrión. Él lo escuchará primero. No se pide nombre real, elige un apodo o quédate anónimo. Los mensajes ofensivos o difamatorios nunca se publicarán.", age: "Confirmo que soy mayor de 18 años", gotit: "Entendido, grabo", tapRec: "Toca para grabar", recording: "Grabando…", restart: "Reiniciar", cont: "Continuar", nickname: "Apodo", nickPh: "Huésped anónimo", country: "País", rating: "Tu valoración", consentQ: "¿Podemos publicar tu saludo en la página de la casa?", yes: "Sí, publícalo", no: "No, privado", sending: "Enviando…", savedTitle: "¡Hecho, gracias!", saveCode: "Guarda este código si quieres pedir la eliminación en el futuro: escribe a", withCode: "con el código", copy: "Copiar código", copied: "¡Copiado!", close: "Cerrar", micDenied: "Permiso de micrófono denegado. Actívalo en los ajustes del navegador.", selCountry: "Selecciona un país" },
  de: { how: "So funktioniert's", howText: "Erzähl in einer Minute, wie es war, grüße die, die nach dir kommen, oder hinterlasse einen Gedanken für den Gastgeber. Er hört es zuerst. Kein echter Name nötig, wähle einen Spitznamen oder bleib anonym. Beleidigende oder verleumderische Nachrichten werden nie veröffentlicht.", age: "Ich bestätige, dass ich über 18 bin", gotit: "Verstanden, aufnehmen", tapRec: "Zum Aufnehmen tippen", recording: "Aufnahme…", restart: "Neu starten", cont: "Weiter", nickname: "Spitzname", nickPh: "Anonymer Gast", country: "Land", rating: "Deine Bewertung", consentQ: "Dürfen wir deinen Gruß auf der Seite der Unterkunft veröffentlichen?", yes: "Ja, veröffentlichen", no: "Nein, privat", sending: "Senden…", savedTitle: "Fertig, danke!", saveCode: "Speichere diesen Code, falls du später eine Entfernung anfragen willst: schreibe an", withCode: "mit dem Code", copy: "Code kopieren", copied: "Kopiert!", close: "Schließen", micDenied: "Mikrofonzugriff verweigert. Aktiviere ihn in den Browsereinstellungen.", selCountry: "Land auswählen" },
};

export default function VoiceRecorder({ propertyId, open, onClose, lang = "it", accent = "#d04260" }) {
  const t = T[lang] || T.it;
  const [step, setStep] = useState("intro"); // intro|record|playback|details|token
  const [ageOk, setAgeOk] = useState(false);
  const [micErr, setMicErr] = useState(false);
  const [recording, setRecording] = useState(false);
  const [remaining, setRemaining] = useState(60);
  const [levels, setLevels] = useState(new Array(28).fill(6));
  const [blob, setBlob] = useState(null);
  const [audioUrl, setAudioUrl] = useState(null);
  const [playing, setPlaying] = useState(false);
  const [nickname, setNickname] = useState("");
  const [country, setCountry] = useState("");
  const [rating, setRating] = useState(0);
  const [sending, setSending] = useState(false);
  const [token, setToken] = useState(null);
  const [copied, setCopied] = useState(false);

  const mediaRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);
  const audioCtxRef = useRef(null);
  const rafRef = useRef(null);
  const timerRef = useRef(null);
  const playerRef = useRef(null);
  const startTsRef = useRef(0);

  const cleanup = useCallback(() => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    if (timerRef.current) clearInterval(timerRef.current);
    try { streamRef.current?.getTracks().forEach(x => x.stop()); } catch {}
    try { audioCtxRef.current?.close(); } catch {}
  }, []);

  useEffect(() => () => cleanup(), [cleanup]);

  const reset = () => {
    cleanup();
    setStep("intro"); setAgeOk(false); setMicErr(false); setRecording(false);
    setRemaining(60); setLevels(new Array(28).fill(6)); setBlob(null);
    if (audioUrl) URL.revokeObjectURL(audioUrl);
    setAudioUrl(null); setPlaying(false); setNickname(""); setCountry(""); setRating(0);
    setSending(false); setToken(null); setCopied(false);
  };

  const handleClose = () => { reset(); onClose?.(); };

  const stopRecording = useCallback(() => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    if (timerRef.current) clearInterval(timerRef.current);
    try { if (mediaRef.current && mediaRef.current.state !== "inactive") mediaRef.current.stop(); } catch {}
    setRecording(false);
  }, []);

  const startRecording = async () => {
    setMicErr(false);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mime = MediaRecorder.isTypeSupported("audio/webm;codecs=opus") ? "audio/webm;codecs=opus"
                 : MediaRecorder.isTypeSupported("audio/webm") ? "audio/webm" : "";
      const mr = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
      chunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      mr.onstop = () => {
        const b = new Blob(chunksRef.current, { type: "audio/webm" });
        setBlob(b);
        setAudioUrl(URL.createObjectURL(b));
        try { streamRef.current?.getTracks().forEach(x => x.stop()); } catch {}
        try { audioCtxRef.current?.close(); } catch {}
        setStep("playback");
      };
      mediaRef.current = mr;
      mr.start();
      setRecording(true);
      setRemaining(60);
      startTsRef.current = Date.now();

      // Waveform via AnalyserNode
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      audioCtxRef.current = ctx;
      const src = ctx.createMediaStreamSource(stream);
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 64;
      src.connect(analyser);
      const data = new Uint8Array(analyser.frequencyBinCount);
      const tick = () => {
        analyser.getByteFrequencyData(data);
        const arr = [];
        for (let i = 0; i < 28; i++) {
          const v = data[i % data.length] || 0;
          arr.push(6 + (v / 255) * 46);
        }
        setLevels(arr);
        rafRef.current = requestAnimationFrame(tick);
      };
      tick();

      // Smooth countdown; at 0 → auto-stop KEEPING the recording
      timerRef.current = setInterval(() => {
        const elapsed = (Date.now() - startTsRef.current) / 1000;
        const left = Math.max(0, 60 - elapsed);
        setRemaining(left);
        if (left <= 0) stopRecording();
      }, 80);
    } catch {
      setMicErr(true);
    }
  };

  const togglePlay = () => {
    const el = playerRef.current;
    if (!el) return;
    if (el.paused) { el.play(); setPlaying(true); } else { el.pause(); setPlaying(false); }
  };

  const submit = async (consent) => {
    if (sending) return;
    setSending(true);
    // Modalità DEMO: non registrare/salvare nulla, mostra solo la conferma.
    if (propertyId === "prop_demo_hostland") {
      setTimeout(() => { setToken("DEMO0000"); setStep("token"); setSending(false); }, 600);
      return;
    }
    try {
      const fd = new FormData();
      fd.append("property_id", propertyId);
      fd.append("rating", String(rating || 8));
      fd.append("guest_consent_public", consent ? "true" : "false");
      fd.append("age_confirmed", "true");
      const elapsed = Math.min(60, Math.max(1, Math.round((Date.now() - startTsRef.current) / 1000)));
      fd.append("duration_seconds", String(elapsed));
      fd.append("country_code", country || "IT");
      fd.append("nickname", nickname || "");
      fd.append("audio", blob, "voice.webm");
      const r = await axios.post(`${API}/voice-reviews`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setToken(r.data.removal_token);
      setStep("token");
    } catch (e) {
      setMicErr(false);
      alert(e?.response?.data?.detail || "Errore invio");
    } finally {
      setSending(false);
    }
  };

  const copyCode = () => { navigator.clipboard?.writeText(token); setCopied(true); setTimeout(() => setCopied(false), 1800); };

  if (!open) return null;
  const mm = Math.floor(remaining / 60), ss = Math.floor(remaining % 60);
  const pct = (remaining / 60);

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="fixed inset-0 z-[9999] bg-zinc-950 text-white flex flex-col overflow-y-auto"
        data-testid="voice-recorder-modal"
      >
        <div className="flex justify-end p-4 sticky top-0">
          <button onClick={handleClose} className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center active:scale-90 transition-transform" data-testid="voice-close-btn"><X className="w-5 h-5" /></button>
        </div>

        <div className="flex-1 flex flex-col justify-center px-6 pb-10 max-w-md w-full mx-auto">
          {step === "intro" && (
            <motion.div initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} data-testid="voice-intro">
              <div className="w-16 h-16 rounded-3xl flex items-center justify-center mb-5" style={{ background: accent }}><Mic className="w-8 h-8" /></div>
              <h2 className="text-3xl font-black mb-3">{t.how}</h2>
              <p className="text-zinc-300 text-[15px] leading-relaxed mb-6">{t.howText}</p>
              <label className="flex items-center gap-3 mb-6 cursor-pointer select-none">
                <input type="checkbox" checked={ageOk} onChange={e => setAgeOk(e.target.checked)} className="w-5 h-5 accent-current" style={{ accentColor: accent }} data-testid="voice-age-check" />
                <span className="text-sm text-zinc-200">{t.age}</span>
              </label>
              <button disabled={!ageOk} onClick={() => setStep("record")}
                className="w-full py-4 rounded-2xl font-black text-[16px] disabled:opacity-40 transition-opacity active:scale-[0.98]"
                style={{ background: accent }} data-testid="voice-gotit-btn">{t.gotit}</button>
            </motion.div>
          )}

          {step === "record" && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-center" data-testid="voice-record">
              {micErr && <p className="text-red-300 text-sm mb-4">{t.micDenied}</p>}
              <div className="relative mx-auto mb-10 w-52 h-52 flex items-center justify-center">
                <svg className="absolute inset-0 -rotate-90" viewBox="0 0 200 200">
                  <circle cx="100" cy="100" r="92" fill="none" stroke="rgba(255,255,255,0.12)" strokeWidth="8" />
                  <circle cx="100" cy="100" r="92" fill="none" stroke={accent} strokeWidth="8" strokeLinecap="round"
                    strokeDasharray={2 * Math.PI * 92}
                    strokeDashoffset={(1 - pct) * 2 * Math.PI * 92}
                    style={{ transition: "stroke-dashoffset 0.09s linear" }} />
                </svg>
                <div className="text-center">
                  <div className="text-5xl font-black tabular-nums">{mm}:{String(ss).padStart(2, "0")}</div>
                  <div className="text-xs text-zinc-400 mt-1">{recording ? t.recording : t.tapRec}</div>
                </div>
              </div>

              <div className="flex items-end justify-center gap-[3px] h-14 mb-10">
                {levels.map((h, i) => (
                  <span key={i} className="w-1.5 rounded-full" style={{ height: `${h}px`, background: recording ? accent : "rgba(255,255,255,0.25)", transition: "height 0.08s ease" }} />
                ))}
              </div>

              {!recording ? (
                <button onClick={startRecording} className="w-20 h-20 rounded-full mx-auto flex items-center justify-center shadow-2xl active:scale-90 transition-transform" style={{ background: accent }} data-testid="voice-start-btn">
                  <Mic className="w-9 h-9" />
                </button>
              ) : (
                <button onClick={stopRecording} className="w-20 h-20 rounded-full mx-auto flex items-center justify-center bg-white active:scale-90 transition-transform" data-testid="voice-stop-btn">
                  <span className="w-7 h-7 rounded-md" style={{ background: accent }} />
                </button>
              )}
            </motion.div>
          )}

          {step === "playback" && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} data-testid="voice-playback">
              <audio ref={playerRef} src={audioUrl} onEnded={() => setPlaying(false)} className="hidden" />
              <div className="rounded-3xl bg-white/5 border border-white/10 p-6 mb-6 flex items-center gap-4">
                <button onClick={togglePlay} className="w-14 h-14 rounded-full flex items-center justify-center shrink-0" style={{ background: accent }} data-testid="voice-play-btn">
                  {playing ? <Pause className="w-6 h-6" /> : <Play className="w-6 h-6 ml-0.5" />}
                </button>
                <div className="flex items-end gap-[3px] h-9 flex-1">
                  {new Array(30).fill(0).map((_, i) => (
                    <span key={i} className="w-1 rounded-full" style={{ height: `${8 + Math.abs(Math.sin(i * 0.9)) * 26}px`, background: "rgba(255,255,255,0.35)" }} />
                  ))}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <button onClick={() => { reset(); setStep("record"); }} className="py-3.5 rounded-2xl font-bold bg-white/10 flex items-center justify-center gap-2 active:scale-95 transition-transform" data-testid="voice-restart-btn"><RotateCcw className="w-4 h-4" /> {t.restart}</button>
                <button onClick={() => setStep("details")} className="py-3.5 rounded-2xl font-black active:scale-95 transition-transform" style={{ background: accent }} data-testid="voice-continue-btn">{t.cont}</button>
              </div>
            </motion.div>
          )}

          {step === "details" && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} data-testid="voice-details">
              <label className="text-xs font-bold text-zinc-400 uppercase tracking-wider">{t.nickname}</label>
              <input value={nickname} onChange={e => setNickname(e.target.value)} placeholder={t.nickPh} maxLength={40}
                className="w-full mt-2 mb-5 bg-white/5 border border-white/10 rounded-2xl px-4 py-3.5 text-white placeholder-zinc-500 outline-none focus:border-white/30" data-testid="voice-nickname-input" />

              <label className="text-xs font-bold text-zinc-400 uppercase tracking-wider">{t.country}</label>
              <div className="relative mt-2 mb-5">
                <select value={country} onChange={e => setCountry(e.target.value)}
                  className="w-full appearance-none bg-white/5 border border-white/10 rounded-2xl px-4 py-3.5 text-white outline-none focus:border-white/30 pr-10" data-testid="voice-country-select">
                  <option value="" className="text-black">{t.selCountry}</option>
                  {COUNTRIES.map(cc => <option key={cc} value={cc} className="text-black">{flagEmoji(cc)} {countryName(cc, lang)}</option>)}
                </select>
                <ChevronDown className="w-5 h-5 absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 pointer-events-none" />
              </div>

              <label className="text-xs font-bold text-zinc-400 uppercase tracking-wider">{t.rating}</label>
              <div className="text-center my-3 text-5xl">{RATING_EMOJI[rating] || "🙂"}</div>
              <div className="flex justify-between gap-1.5 mb-6" data-testid="voice-rating">
                {[1,2,3,4,5,6,7,8,9,10].map(n => (
                  <button key={n} onClick={() => setRating(n)}
                    className="flex-1 aspect-square rounded-full text-xs font-bold transition-all active:scale-90"
                    style={{ background: n <= rating ? accent : "rgba(255,255,255,0.08)", color: n <= rating ? "#fff" : "#a1a1aa" }}
                    data-testid={`voice-rating-${n}`}>{n}</button>
                ))}
              </div>

              <p className="text-center text-[15px] font-bold mb-4">{t.consentQ}</p>
              <div className="grid grid-cols-1 gap-3">
                <button disabled={sending || !country} onClick={() => submit(true)} className="py-4 rounded-2xl font-black disabled:opacity-40 active:scale-[0.98] transition-transform" style={{ background: accent }} data-testid="voice-consent-yes">{sending ? t.sending : t.yes}</button>
                <button disabled={sending || !country} onClick={() => submit(false)} className="py-4 rounded-2xl font-bold bg-white/10 disabled:opacity-40 active:scale-[0.98] transition-transform" data-testid="voice-consent-no">{sending ? t.sending : t.no}</button>
              </div>
            </motion.div>
          )}

          {step === "token" && (
            <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="text-center" data-testid="voice-token">
              <div className="w-16 h-16 rounded-full bg-emerald-500 flex items-center justify-center mx-auto mb-5"><Check className="w-9 h-9" /></div>
              <h2 className="text-2xl font-black mb-3">{t.savedTitle}</h2>
              <div className="rounded-3xl bg-white/5 border border-white/10 p-6 mb-5">
                <div className="text-4xl font-black tracking-[0.2em] mb-3" style={{ color: accent }} data-testid="voice-token-code">{token}</div>
                <button onClick={copyCode} className="inline-flex items-center gap-2 text-sm font-bold px-4 py-2 rounded-full bg-white/10 active:scale-95 transition-transform" data-testid="voice-copy-btn"><Copy className="w-4 h-4" /> {copied ? t.copied : t.copy}</button>
              </div>
              <p className="text-zinc-400 text-xs leading-relaxed mb-6">{t.saveCode} <b>vitadahost@gmail.com</b> {t.withCode} <b>{token}</b></p>
              <button onClick={handleClose} className="w-full py-4 rounded-2xl font-black" style={{ background: accent }} data-testid="voice-done-btn">{t.close}</button>
            </motion.div>
          )}
        </div>
      </motion.div>
    </AnimatePresence>
  );
}
